import {
  type CompareMetricBlueprint,
  type MetricBlueprint,
  withCompareMetricPaths,
  withMetricPaths,
} from "@/lib/ai/visualization/data/adapters/metric-binding";

function ratioAggregation(numeratorKey: string, denominatorKey: string) {
  return {
    kind: "ratio" as const,
    numeratorKey,
    denominatorKey,
  };
}

function movingAverageMetric(
  metric: MetricBlueprint,
  options: {
    metricFamily: string;
    movingAverageWindowDays: 7 | 28;
  },
): MetricBlueprint {
  return {
    ...metric,
    lineInterpolation: "monotoneX",
    metricFamily: options.metricFamily,
    movingAverageWindowDays: options.movingAverageWindowDays,
  };
}

/**
 * UA aggregated 系 dataset が共通で使う基本指標定義。
 */
export const UA_BASE_METRICS: MetricBlueprint[] = withMetricPaths([
  { key: "sessionCount", unit: "count" },
  { key: "uniqueUserCount", unit: "count" },
  { key: "conversionCount", unit: "count" },
  {
    key: "avgSessionDuration",
    unit: "duration",
    format: "duration",
  },
  {
    key: "conversionRate",
    unit: "percent",
    aggregation: ratioAggregation("conversionCount", "sessionCount"),
  },
  { key: "surfCount", unit: "count" },
  {
    key: "surfRate",
    unit: "percent",
    aggregation: ratioAggregation("surfCount", "sessionCount"),
  },
  { key: "engagementCount", unit: "count" },
  {
    key: "engagementRate",
    unit: "percent",
    aggregation: ratioAggregation("engagementCount", "sessionCount"),
  },
  { key: "pageViewEventCount", unit: "count" },
]);

const UA_MOVING_AVERAGE_METRICS: MetricBlueprint[] = withMetricPaths([
  movingAverageMetric(
    {
      key: "sc7d",
      unit: "count",
      aliases: ["sessionCount7d"],
    },
    { metricFamily: "sessionCount", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "sc28d",
      unit: "count",
      aliases: ["sessionCount28d"],
    },
    { metricFamily: "sessionCount", movingAverageWindowDays: 28 },
  ),
  movingAverageMetric(
    {
      key: "uuc7d",
      unit: "count",
      aliases: ["uniqueUserCount7d"],
    },
    { metricFamily: "uniqueUserCount", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "uuc28d",
      unit: "count",
      aliases: ["uniqueUserCount28d"],
    },
    { metricFamily: "uniqueUserCount", movingAverageWindowDays: 28 },
  ),
  movingAverageMetric(
    {
      key: "cc7d",
      unit: "count",
      aliases: ["conversionCount7d"],
    },
    { metricFamily: "conversionCount", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "cc28d",
      unit: "count",
      aliases: ["conversionCount28d"],
    },
    { metricFamily: "conversionCount", movingAverageWindowDays: 28 },
  ),
  movingAverageMetric(
    {
      key: "cr7d",
      unit: "percent",
      aliases: ["conversionRate7d"],
      aggregation: ratioAggregation("cc7d", "sc7d"),
    },
    { metricFamily: "conversionRate", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "cr28d",
      unit: "percent",
      aliases: ["conversionRate28d"],
      aggregation: ratioAggregation("cc28d", "sc28d"),
    },
    { metricFamily: "conversionRate", movingAverageWindowDays: 28 },
  ),
  movingAverageMetric(
    {
      key: "sfc7d",
      unit: "count",
      aliases: ["surfCount7d"],
    },
    { metricFamily: "surfCount", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "sfc28d",
      unit: "count",
      aliases: ["surfCount28d"],
    },
    { metricFamily: "surfCount", movingAverageWindowDays: 28 },
  ),
  movingAverageMetric(
    {
      key: "sr7d",
      unit: "percent",
      aliases: ["surfRate7d"],
      aggregation: ratioAggregation("sfc7d", "sc7d"),
    },
    { metricFamily: "surfRate", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "sr28d",
      unit: "percent",
      aliases: ["surfRate28d"],
      aggregation: ratioAggregation("sfc28d", "sc28d"),
    },
    { metricFamily: "surfRate", movingAverageWindowDays: 28 },
  ),
  movingAverageMetric(
    {
      key: "ec7d",
      unit: "count",
      aliases: ["engagementCount7d"],
    },
    { metricFamily: "engagementCount", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "ec28d",
      unit: "count",
      aliases: ["engagementCount28d"],
    },
    { metricFamily: "engagementCount", movingAverageWindowDays: 28 },
  ),
  movingAverageMetric(
    {
      key: "er7d",
      unit: "percent",
      aliases: ["engagementRate7d"],
      aggregation: ratioAggregation("ec7d", "sc7d"),
    },
    { metricFamily: "engagementRate", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "er28d",
      unit: "percent",
      aliases: ["engagementRate28d"],
      aggregation: ratioAggregation("ec28d", "sc28d"),
    },
    { metricFamily: "engagementRate", movingAverageWindowDays: 28 },
  ),
  movingAverageMetric(
    {
      key: "pve7d",
      unit: "count",
      aliases: ["pageViewEventCount7d"],
    },
    { metricFamily: "pageViewEventCount", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "pve28d",
      unit: "count",
      aliases: ["pageViewEventCount28d"],
    },
    { metricFamily: "pageViewEventCount", movingAverageWindowDays: 28 },
  ),
  movingAverageMetric(
    {
      key: "asd7d",
      unit: "duration",
      format: "duration",
      aliases: ["avgSessionDuration7d"],
    },
    { metricFamily: "avgSessionDuration", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "asd28d",
      unit: "duration",
      format: "duration",
      aliases: ["avgSessionDuration28d"],
    },
    { metricFamily: "avgSessionDuration", movingAverageWindowDays: 28 },
  ),
]);

/**
 * UA aggregated timeseries dataset 向けの指標定義（基本指標 + 移動平均）。
 */
export const UA_TIMESERIES_METRICS: MetricBlueprint[] = [
  ...UA_BASE_METRICS,
  ...UA_MOVING_AVERAGE_METRICS,
];

/**
 * 比較用 moving average を、対応する基礎metricと同じsemantic familyへ正規化する。
 * 省略keyを独立familyとして扱うと、1つの要求metricだけでscatterが成立してしまう。
 */
function movingAverageCompareMetric(
  metric: CompareMetricBlueprint,
): CompareMetricBlueprint {
  const sourceMetric = UA_TIMESERIES_METRICS.find(
    (candidate) => candidate.key === metric.sourceKey,
  );
  if (!sourceMetric?.metricFamily || !sourceMetric.movingAverageWindowDays) {
    throw new Error(
      `UA comparison moving average '${metric.sourceKey}' requires canonical family metadata`,
    );
  }
  return {
    ...metric,
    lineInterpolation: "monotoneX",
    metricFamily: sourceMetric.metricFamily,
    movingAverageWindowDays: sourceMetric.movingAverageWindowDays,
  };
}

/**
 * UA compare 系 dataset が共有する current/previous 指標定義。
 */
const UA_COMPARE_BASE_METRICS: CompareMetricBlueprint[] =
  withCompareMetricPaths([
    {
      sourceKey: "sessionCount",
      currentKey: "currentSessionCount",
      previousKey: "previousSessionCount",
      unit: "count",
      baseAliases: ["sessionCount"],
    },
    {
      sourceKey: "uniqueUserCount",
      currentKey: "currentUniqueUserCount",
      previousKey: "previousUniqueUserCount",
      unit: "count",
      baseAliases: ["uniqueUserCount"],
    },
    {
      sourceKey: "conversionCount",
      currentKey: "currentConversionCount",
      previousKey: "previousConversionCount",
      unit: "count",
      baseAliases: ["conversionCount"],
    },
    {
      sourceKey: "avgSessionDuration",
      currentKey: "currentAvgSessionDuration",
      previousKey: "previousAvgSessionDuration",
      unit: "duration",
      format: "duration",
      baseAliases: ["avgSessionDuration"],
    },
    {
      sourceKey: "conversionRate",
      currentKey: "currentConversionRate",
      previousKey: "previousConversionRate",
      unit: "percent",
      currentAggregation: ratioAggregation(
        "currentConversionCount",
        "currentSessionCount",
      ),
      previousAggregation: ratioAggregation(
        "previousConversionCount",
        "previousSessionCount",
      ),
      baseAliases: ["conversionRate"],
    },
    {
      sourceKey: "surfCount",
      currentKey: "currentSurfCount",
      previousKey: "previousSurfCount",
      unit: "count",
      baseAliases: ["surfCount"],
    },
    {
      sourceKey: "surfRate",
      currentKey: "currentSurfRate",
      previousKey: "previousSurfRate",
      unit: "percent",
      currentAggregation: ratioAggregation(
        "currentSurfCount",
        "currentSessionCount",
      ),
      previousAggregation: ratioAggregation(
        "previousSurfCount",
        "previousSessionCount",
      ),
      baseAliases: ["surfRate"],
    },
    {
      sourceKey: "engagementCount",
      currentKey: "currentEngagementCount",
      previousKey: "previousEngagementCount",
      unit: "count",
      baseAliases: ["engagementCount"],
    },
    {
      sourceKey: "engagementRate",
      currentKey: "currentEngagementRate",
      previousKey: "previousEngagementRate",
      unit: "percent",
      currentAggregation: ratioAggregation(
        "currentEngagementCount",
        "currentSessionCount",
      ),
      previousAggregation: ratioAggregation(
        "previousEngagementCount",
        "previousSessionCount",
      ),
      baseAliases: ["engagementRate"],
    },
    {
      sourceKey: "pageViewEventCount",
      currentKey: "currentPageViewEventCount",
      previousKey: "previousPageViewEventCount",
      unit: "count",
      baseAliases: ["pageViewEventCount"],
    },
  ]);

/**
 * timeseries compare dataset 用の compare 指標定義（基本指標 + 移動平均）。
 */
export const UA_COMPARE_TIMESERIES_METRICS: CompareMetricBlueprint[] =
  withCompareMetricPaths([
    ...UA_COMPARE_BASE_METRICS,
    movingAverageCompareMetric({
      sourceKey: "sc7d",
      currentKey: "currentSc7d",
      previousKey: "previousSc7d",
      unit: "count",
      baseAliases: ["sc7d", "sessionCount7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "sc28d",
      currentKey: "currentSc28d",
      previousKey: "previousSc28d",
      unit: "count",
      baseAliases: ["sc28d", "sessionCount28d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "uuc7d",
      currentKey: "currentUuc7d",
      previousKey: "previousUuc7d",
      unit: "count",
      baseAliases: ["uuc7d", "uniqueUserCount7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "uuc28d",
      currentKey: "currentUuc28d",
      previousKey: "previousUuc28d",
      unit: "count",
      baseAliases: ["uuc28d", "uniqueUserCount28d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "cc7d",
      currentKey: "currentCc7d",
      previousKey: "previousCc7d",
      unit: "count",
      baseAliases: ["cc7d", "conversionCount7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "cc28d",
      currentKey: "currentCc28d",
      previousKey: "previousCc28d",
      unit: "count",
      baseAliases: ["cc28d", "conversionCount28d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "cr7d",
      currentKey: "currentCr7d",
      previousKey: "previousCr7d",
      unit: "percent",
      currentAggregation: ratioAggregation("currentCc7d", "currentSc7d"),
      previousAggregation: ratioAggregation("previousCc7d", "previousSc7d"),
      baseAliases: ["cr7d", "conversionRate7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "cr28d",
      currentKey: "currentCr28d",
      previousKey: "previousCr28d",
      unit: "percent",
      currentAggregation: ratioAggregation("currentCc28d", "currentSc28d"),
      previousAggregation: ratioAggregation("previousCc28d", "previousSc28d"),
      baseAliases: ["cr28d", "conversionRate28d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "sfc7d",
      currentKey: "currentSfc7d",
      previousKey: "previousSfc7d",
      unit: "count",
      baseAliases: ["sfc7d", "surfCount7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "sfc28d",
      currentKey: "currentSfc28d",
      previousKey: "previousSfc28d",
      unit: "count",
      baseAliases: ["sfc28d", "surfCount28d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "sr7d",
      currentKey: "currentSr7d",
      previousKey: "previousSr7d",
      unit: "percent",
      currentAggregation: ratioAggregation("currentSfc7d", "currentSc7d"),
      previousAggregation: ratioAggregation("previousSfc7d", "previousSc7d"),
      baseAliases: ["sr7d", "surfRate7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "sr28d",
      currentKey: "currentSr28d",
      previousKey: "previousSr28d",
      unit: "percent",
      currentAggregation: ratioAggregation("currentSfc28d", "currentSc28d"),
      previousAggregation: ratioAggregation("previousSfc28d", "previousSc28d"),
      baseAliases: ["sr28d", "surfRate28d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "ec7d",
      currentKey: "currentEc7d",
      previousKey: "previousEc7d",
      unit: "count",
      baseAliases: ["ec7d", "engagementCount7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "ec28d",
      currentKey: "currentEc28d",
      previousKey: "previousEc28d",
      unit: "count",
      baseAliases: ["ec28d", "engagementCount28d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "er7d",
      currentKey: "currentEr7d",
      previousKey: "previousEr7d",
      unit: "percent",
      currentAggregation: ratioAggregation("currentEc7d", "currentSc7d"),
      previousAggregation: ratioAggregation("previousEc7d", "previousSc7d"),
      baseAliases: ["er7d", "engagementRate7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "er28d",
      currentKey: "currentEr28d",
      previousKey: "previousEr28d",
      unit: "percent",
      currentAggregation: ratioAggregation("currentEc28d", "currentSc28d"),
      previousAggregation: ratioAggregation("previousEc28d", "previousSc28d"),
      baseAliases: ["er28d", "engagementRate28d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "pve7d",
      currentKey: "currentPve7d",
      previousKey: "previousPve7d",
      unit: "count",
      baseAliases: ["pve7d", "pageViewEventCount7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "pve28d",
      currentKey: "currentPve28d",
      previousKey: "previousPve28d",
      unit: "count",
      baseAliases: ["pve28d", "pageViewEventCount28d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "asd7d",
      currentKey: "currentAsd7d",
      previousKey: "previousAsd7d",
      unit: "duration",
      format: "duration",
      baseAliases: ["asd7d", "avgSessionDuration7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "asd28d",
      currentKey: "currentAsd28d",
      previousKey: "previousAsd28d",
      unit: "duration",
      format: "duration",
      baseAliases: ["asd28d", "avgSessionDuration28d"],
    }),
  ]);
